import React, { useState, useEffect } from 'react';
import cacheAPIsService from '../hooks/cacheAPIs';
import AppLoading from '../components/AppLoading';

interface CacheStats {
  size: number;
  keys: string[];
  nextExpiry?: string;
  cacheType: string;
}

interface NextSyncInfo {
  nextSync: string;
  timeUntilSync: string;
}

interface CacheStatsResponse {
  cacheStats: CacheStats;
  nextSyncInfo: NextSyncInfo;
  message: string;
  cacheType: string;
  serverTime: string;
}

const CachePage: React.FC = () => {
  const [cacheData, setCacheData] = useState<CacheStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clearingCache, setClearingCache] = useState(false);
  const [deletingKeys, setDeletingKeys] = useState<Set<string>>(new Set());
  const [clearingDashboard, setClearingDashboard] = useState<string | null>(null);
  const [cacheStatus, setCacheStatus] = useState<'Active' | 'Offline'>('Offline');

  const fetchCacheStats = async () => {
    try {
      setError(null);
      const data = await cacheAPIsService.getCacheStats();
      setCacheData(data);
      // Mark as active only if cache type is 'Redis'
      setCacheStatus(data.cacheType === 'Redis' ? 'Active' : 'Offline');
    } catch (err: any) {
      setError(err.message || 'Failed to fetch cache statistics');
      // Check if it's a 500 error to determine offline status
      if (err.response?.status === 500) {
        setCacheStatus('Offline');
      }
    }
  };

  const handleClearCache = async () => {
    try {
      setClearingCache(true);
      await cacheAPIsService.clearCache();
      // Refresh cache stats after clearing
      await fetchCacheStats();
    } catch (err: any) {
      setError(err.message || 'Failed to clear cache');
    } finally {
      setClearingCache(false);
    }
  };

  const handleDeleteKey = async (key: string) => {
    try {
      setDeletingKeys(prev => new Set(prev).add(key));
      await cacheAPIsService.deleteCacheByKey(key);
      // Refresh cache stats after deleting
      await fetchCacheStats();
    } catch (err: any) {
      setError(err.message || 'Failed to delete cache key');
    } finally {
      setDeletingKeys(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  const handleClearDashboardCache = async (dashboard: string) => {
    try {
      setClearingDashboard(dashboard);
      await cacheAPIsService.clearCacheByDashboard(dashboard);
      // Refresh cache stats after clearing
      await fetchCacheStats();
    } catch (err: any) {
      setError(err.message || 'Failed to clear dashboard cache');
    } finally {
      setClearingDashboard(null);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        // Set initial cache status to Offline, will be updated by fetchCacheStats
        setCacheStatus('Offline');
        await fetchCacheStats();
      } catch (err: any) {
        // Handle error silently - don't change the view
        console.warn('Failed to initialize data:', err.message);
        setCacheStatus('Offline');
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  // Auto-refresh cache stats every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchCacheStats();
    }, 15000); // 15 seconds (15 * 1000 ms)

    return () => clearInterval(interval);
  }, []);

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatCacheKey = (key: string) => {
    // Decode base64 part of the key for better readability
    try {
      const parts = key.split(':');
      if (parts.length === 2) {
        const decoded = Buffer.from(parts[1], 'base64').toString();
        const parsed = JSON.parse(decoded);
        return `${parts[0]}: ${JSON.stringify(parsed, null, 2)}`;
      }
    } catch (e) {
      // If decoding fails, return the original key
    }
    return key;
  };

  const calculateTimeUntilExpiry = (expiryDate: string, serverTime?: string) => {
    const now = serverTime ? new Date(serverTime) : new Date();
    const expiry = new Date(expiryDate);
    const diffMs = expiry.getTime() - now.getTime();
    
    if (diffMs <= 0) {
      return 'Expired';
    }
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const diffSeconds = Math.floor((diffMs % (1000 * 60)) / 1000);
    
    if (diffHours > 0) {
      return `in ${diffHours}h ${diffMinutes}m`;
    } else if (diffMinutes > 0) {
      return `in ${diffMinutes}m ${diffSeconds}s`;
    } else {
      return `in ${diffSeconds}s`;
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <AppLoading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-card">
          <div className="error-content">
            <h2>Error</h2>
            <p>{error}</p>
            <button
              onClick={fetchCacheStats}
              className="retry-btn"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stats-page">
      <div className="layout-header">
        <div className="header-content">
          <h2>Cache Statistics</h2>
          <p>Monitor and manage application cache performance</p>
        </div>
        {/* Commented out direct navigation buttons as requested */}
        {/* 
        <div className="navigation-buttons">
          <button 
            onClick={() => window.location.href = '/'}
            className="nav-btn nav-btn-primary"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Go to Dashboards
          </button>
          <button 
            onClick={() => window.location.href = '/cron-server'}
            className="nav-btn nav-btn-secondary"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Go to Cron Server
          </button>
        </div>
        */}
      </div>

      <div className="auto-refresh-status">
        <span className="status-indicator">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="status-icon">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Page Auto-refresh: Every 15 seconds
        </span>
        {cacheData?.serverTime && (
          <div style={{ textAlign: "left", color: '#4a5568', fontWeight: '500', marginTop: 8, paddingLeft: '2px', fontSize: '0.9rem'}}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width: '16px', height: '16px', marginRight: '6px', marginBottom: '-2.5px', display: 'inline'}}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Server Time: {new Date(cacheData.serverTime).toLocaleString('en-GB', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric',
              hour: '2-digit', 
              minute: '2-digit',
              second: '2-digit'
            })}
          </div>
        )}
        <div style={{ textAlign: "left", color: cacheStatus === 'Active' ? '#10b981' : '#ef4444', fontWeight: '600', marginTop: 10, display: 'flex', gap: '8px'}}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px'}}>
            {cacheStatus === 'Active' ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            )}
          </svg>
          Cache Status: {cacheStatus}
        </div>
        {cacheData?.cacheStats.nextExpiry && (
          <div style={{ textAlign: "left", color: '#667eea', fontWeight: '600', marginTop: 10}}>
            Next cache expiry: {new Date(cacheData.cacheStats.nextExpiry).toLocaleDateString('en-GB', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric' 
            })} at {new Date(cacheData.cacheStats.nextExpiry).toLocaleTimeString('en-GB', { 
              hour: '2-digit', 
              minute: '2-digit',
              second: '2-digit'
            })} ({calculateTimeUntilExpiry(cacheData.cacheStats.nextExpiry, cacheData.serverTime)})
          </div>
        )}
      </div>

      <div className="layout-content">

        {cacheData && (
          <div>
            {/* Cache Overview Section */}
            <div className="cache-overview-section">
              <div className="section-header">
                <h2>Cache Overview</h2>
                <button
                  onClick={fetchCacheStats}
                  className="btn btn-secondary"
                  disabled={clearingCache}
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
              </div>

              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-content">
                    <div className="stat-header">
                      <div className="stat-icon">
                        <div className="icon-container blue">
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                        </div>
                      </div>
                      <div className="stat-info">
                        <dl>
                          <dt>Cached Keys</dt>
                          <dd>{cacheData.cacheStats.keys?.length?.toLocaleString() || '0'}</dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-content">
                    <div className="stat-header">
                      <div className="stat-icon">
                        <div className="icon-container purple">
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                      </div>
                      <div className="stat-info">
                        <dl>
                          <dt>Cache Size</dt>
                          <dd>{formatBytes(cacheData.cacheStats.size)}</dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-content">
                    <div className="stat-header">
                      <div className="stat-icon">
                        <div className="icon-container green">
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="stat-info">
                        <dl>
                          <dt>Next Expiry</dt>
                          <dd>{cacheData.cacheStats.nextExpiry ? new Date(cacheData.cacheStats.nextExpiry).toLocaleString() : 'N/A'}</dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-content">
                    <div className="stat-header">
                      <div className="stat-icon">
                        <div className="icon-container orange">
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                      </div>
                      <div className="stat-info">
                        <dl>
                          <dt>Next Sync</dt>
                          <dd>{cacheData?.nextSyncInfo?.timeUntilSync}</dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-content">
                    <div className="stat-header">
                      <div className="stat-icon">
                        <div className="icon-container teal">
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                          </svg>
                        </div>
                      </div>
                      <div className="stat-info">
                        <dl>
                          <dt>Cache Type</dt>
                          <dd>{cacheData.cacheType}</dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Metrics 
            <div className="metrics-section">
              <div className="section-header">
                <h2>Performance Metrics</h2>
              </div>
              <div className="metrics-content">
                <div className="metrics-grid">
                  <div className="metric-item">
                    <h4>Total Cache Entries</h4>
                    <div className="metric-value">
                      {cacheData.cacheStats.keys?.length?.toLocaleString() || '0'}
                    </div>
                    <div className="metric-description">
                      Number of cached items currently stored
                    </div>
                  </div>
                  <div className="metric-item">
                    <h4>Cache Type</h4>
                    <div className="metric-value">
                      {cacheData.cacheType}
                    </div>
                    <div className="metric-description">
                      {cacheData.cacheType === 'Redis' ? 'External Redis cache for better performance' : 'Internal in-memory cache (fallback mode)'}
                    </div>
                  </div>
                  <div className="metric-item">
                    <h4>Next Sync</h4>
                    <div className="metric-value">
                      {cacheData.nextSyncInfo.timeUntilSync}
                    </div>
                    <div className="metric-description">
                      {new Date(cacheData.nextSyncInfo.nextSync)?.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>*/}
            {/* Cache Keys List */}
            {cacheData.cacheStats.keys && cacheData.cacheStats.keys.length > 0 && (
              <div className="keys-section">
                <div className="section-header">
                  <h2>Cache Keys ({cacheData.cacheStats.keys.length})</h2>
                </div>
                <div className="keys-content">
                  <div className="keys-list-container">
                    <div className="keys-list">
                      {cacheData.cacheStats.keys.map((key, index) => (
                        <div key={index} className="key-item">
                          <div className="key-content">
                            <span className="key-text">{formatCacheKey(key)}</span>
                            <span className="key-index">#{index + 1}</span>
                            <button
                              onClick={() => handleDeleteKey(key)}
                              disabled={deletingKeys.has(key)}
                              className="delete-key-btn"
                              title="Delete this cache key"
                            >
                              {deletingKeys.has(key) ? (
                                <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}


            {/* Dashboard Cache Management */}
            <div className="dashboard-management-section">
              <div className="section-header">
                <h2>Cache Management <i>(Clear)</i></h2>
              </div>
              <div className="dashboard-management-content">
                  
                <div className="dashboard-buttons-grid">
                  <button
                    onClick={handleClearCache}
                    disabled={clearingCache}
                    className="btn btn-danger btn-large"
                  >
                    {clearingCache ? (
                      <>
                        <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Clearing All Cache...
                      </>
                    ) : (
                      <>
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Clear All Cache
                      </>
                    )}
                  </button>
                  {[1, 2, 3, 4, 5, 6].map((dashboardNum) => (
                    <button
                      key={dashboardNum}
                      onClick={() => handleClearDashboardCache(`dashboard${dashboardNum}`)}
                      disabled={clearingDashboard === `dashboard${dashboardNum}`}
                      className="btn btn-dashboard"
                    >
                      {clearingDashboard === `dashboard${dashboardNum}` ? (
                        <>
                          <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Clearing...
                        </>
                      ) : (
                        <>
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          Dashboard {dashboardNum}
                        </>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Status Message 
            <div className="status-message">
              <div className="message-content">
                <div className="message-icon">
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="message-text">
                  <p>{cacheData.message}</p>
                </div>
              </div>
            </div>*/}
          </div>
        )}
      </div>
    </div>
  );
};

export default CachePage; 